import { Box } from '@mantine/core'
import { clsx } from '@mantine/styles'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button, Flex, Icon, Text } from '@percent/lemonade'
import {
  canLogGivingAction,
  isGivingActionCompleted
} from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCard'
import type { GivingActionRow } from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCard.types'
import { useGivingActionRewardValue } from '@percent/workplace-giving/common/hooks/useGivingActionRewardValue/useGivingActionRewardValue'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import classes from './UpcomingGivingActionItem.module.scss'

export type UpcomingGivingActionItemProps = Readonly<{
  row: GivingActionRow
  onLogAction: (row: GivingActionRow) => void
  onViewAction: (row: GivingActionRow) => void
  isLogging?: boolean
  justCompleted?: boolean
}>

export function UpcomingGivingActionItem({
  row,
  onLogAction,
  onViewAction,
  isLogging = false,
  justCompleted = false
}: UpcomingGivingActionItemProps) {
  const { t } = useTranslation()
  const { isLoading: isRewardLoading, formatReward } = useGivingActionRewardValue()
  const completed = isGivingActionCompleted(row, justCompleted)
  const showCta = canLogGivingAction(row, justCompleted)
  const showLoggedCount = row.frequency === 'daily' && row.actionLogCompletedCount > 0
  const rewardValue = formatReward(row.pointValue, row.rewardsEligible)

  return (
    <Box className={classes.Wrapper} data-giving-action-row-id={row.id} data-testid="upcoming-giving-action-item">
      <Box className={classes.Content}>
        <button
          type="button"
          className={classes.TitleButton}
          onClick={() => onViewAction(row)}
          data-testid="upcoming-giving-action-title"
        >
          <Text className={classes.Title}>{row.name}</Text>
        </button>
        <Box className={classes.MetaRow}>
          {isRewardLoading ? null : (
            <Text size="small" className={clsx(classes.Meta, classes.MetaItem)}>
              {rewardValue
                ? t('workplace_giving.givingActions.pointsRewardValue', { value: rewardValue })
                : t('workplace_giving.givingActions.points', { count: row.pointValue })}
            </Text>
          )}
          <Text size="small" className={clsx(classes.Meta, classes.MetaItem)}>
            {t(`workplace_giving.givingActions.frequency.${row.frequency}`)}
          </Text>
          {row.target.name ? (
            <Link
              className={clsx(classes.CampaignLink, classes.MetaItem)}
              to={AppRoute.CAMPAIGN.replace(':campaignId', row.target.id)}
            >
              {row.target.name}
            </Link>
          ) : null}
          {showLoggedCount ? (
            <Text size="small" className={clsx(classes.Meta, classes.MetaItem)}>
              {t('workplace_giving.givingActions.loggedTimes', { count: row.actionLogCompletedCount })}
            </Text>
          ) : null}
        </Box>
      </Box>

      {completed ? (
        <Flex align="center" gap="0.5rem" c="rgba(var(--success400))" data-testid="giving-action-completed-state">
          <Icon name="check" size={5} color="success400" />
          <Text size="small" fontWeight="medium" color="rgba(var(--success400))">
            {t('workplace_giving.givingActions.completed')}
          </Text>
        </Flex>
      ) : null}
      {showCta ? (
        <Box className={classes.ActionSlot}>
          <Button
            size="small"
            shape="oval"
            variant="secondary"
            loading={isLogging}
            disabled={isLogging}
            onClick={() => onLogAction(row)}
          >
            {t(
              row.frequency === 'daily'
                ? 'workplace_giving.givingActions.logCompletion'
                : 'workplace_giving.givingActions.markAsDone'
            )}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
